import React from "react";
import type { ProviderId } from "@/api/integrations/links";

export interface ProviderMeta {
    id: ProviderId;
    vendorKey: string;
    name: string;
    descriptor?: string;
    flagName: string;
    kind: "tracker" | "git";
    tile: { bg: string; border: string; fg: string };
    logo: React.ReactNode;
    columns: { name: string; key: string };
    connectedColumnLabel: string;
    connectField?: { label: string; placeholder: string; helperText: string; required: boolean };
}

const JiraLogo = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M11.53 2H22v10.4a4.6 4.6 0 0 1-4.6 4.6h-2.93V11.6a4.6 4.6 0 0 0-4.6-4.6H6.94A4.6 4.6 0 0 1 11.53 2z" opacity=".55" />
        <path d="M6.6 7H17v10.4a4.6 4.6 0 0 1-4.6 4.6H9.47V16.6a4.6 4.6 0 0 0-4.6-4.6H2A4.6 4.6 0 0 1 6.6 7z" />
    </svg>
);

const GitHubLogo = (
    <svg width="21" height="21" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2a10 10 0 0 0-3.16 19.49c.5.09.68-.22.68-.48v-1.7c-2.78.6-3.37-1.34-3.37-1.34-.45-1.16-1.11-1.47-1.11-1.47-.91-.62.07-.61.07-.61 1 .07 1.53 1.03 1.53 1.03.89 1.53 2.34 1.09 2.91.83.09-.65.35-1.09.63-1.34-2.22-.25-4.55-1.11-4.55-4.94 0-1.09.39-1.98 1.03-2.68-.1-.25-.45-1.27.1-2.65 0 0 .84-.27 2.75 1.02a9.55 9.55 0 0 1 5 0c1.91-1.29 2.75-1.02 2.75-1.02.55 1.38.2 2.4.1 2.65.64.7 1.03 1.59 1.03 2.68 0 3.84-2.34 4.69-4.57 4.94.36.31.68.92.68 1.85v2.74c0 .27.18.58.69.48A10 10 0 0 0 12 2z" />
    </svg>
);

const AzureDevOpsLogo = (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18.6 3 4.5 6.2 3 15.8 9.4 21 21 18V6z" />
        <path d="M8 8.5v7l5 1.5" />
    </svg>
);

const GitLabLogo = (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 21 3 14.5l1.6-5h4.2L12 21z" opacity=".9" />
        <path d="m12 21 9-6.5-1.6-5h-4.2L12 21z" opacity=".6" />
    </svg>
);

const BitbucketLogo = (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="currentColor">
        <path d="M3 3h18l-2.4 15.6L12 21l-6.6-2.4z" opacity=".85" />
    </svg>
);

export const PROVIDERS: ProviderMeta[] = [
    {
        id: "jira", vendorKey: "atlassian_jira.OAUTH", name: "Jira",
        flagName: "integrate-jira", kind: "tracker",
        tile: { bg: "#eff6ff", border: "#bfdbfe", fg: "#1d4ed8" },
        logo: JiraLogo,
        columns: { name: "Jira Project", key: "Key" },
        connectedColumnLabel: "Jira Project · Key",
        connectField: {
            label: "Atlassian Domain", placeholder: "your-company.atlassian.net",
            helperText: "Enter your Atlassian domain without https://", required: true
        }
    },
    {
        id: "github", vendorKey: "github.OAUTH", name: "GitHub",
        flagName: "integrate-github", kind: "git",
        tile: { bg: "#f4f4f5", border: "#e2e8f0", fg: "#111827" },
        logo: GitHubLogo,
        columns: { name: "Repository", key: "Branch" },
        connectedColumnLabel: "Repository · Branch"
    },
    {
        id: "azure-devops", vendorKey: "azure_devops_boards.OAUTH", name: "Azure DevOps",
        descriptor: "Boards", flagName: "integrate-azure", kind: "tracker",
        tile: { bg: "#eff6ff", border: "#bfdbfe", fg: "#0067b8" },
        logo: AzureDevOpsLogo,
        columns: { name: "Project", key: "Id" },
        connectedColumnLabel: "Project · Org",
        connectField: {
            label: "Organization URL", placeholder: "https://dev.azure.com/your-org",
            helperText: "Full Azure DevOps organization URL", required: true
        }
    },
    {
        id: "azure-repos", vendorKey: "azure_repos.OAUTH", name: "Azure Repos",
        descriptor: "Git repositories", flagName: "integrate-azure", kind: "git",
        tile: { bg: "#eff6ff", border: "#bfdbfe", fg: "#0067b8" },
        logo: AzureDevOpsLogo,
        columns: { name: "Repository", key: "Branch" },
        connectedColumnLabel: "Repository · Branch",
        connectField: {
            label: "Organization URL", placeholder: "https://dev.azure.com/your-org",
            helperText: "Full Azure DevOps organization URL", required: true
        }
    },
    {
        id: "gitlab", vendorKey: "gitlab.OAUTH", name: "GitLab",
        flagName: "integrate-gitlab", kind: "git",
        tile: { bg: "#fef3e8", border: "#fed7aa", fg: "#e2570f" },
        logo: GitLabLogo,
        columns: { name: "Repository", key: "Branch" },
        connectedColumnLabel: "Repository · Branch",
        connectField: {
            label: "GitLab URL (Optional)", placeholder: "https://gitlab.com (or self-hosted URL)",
            helperText: "Leave empty for gitlab.com or enter your self-hosted GitLab URL", required: false
        }
    },
    {
        id: "bitbucket", vendorKey: "bitbucket.OAUTH", name: "Bitbucket",
        flagName: "integrate-bitbucket", kind: "git",
        tile: { bg: "#eff6ff", border: "#bfdbfe", fg: "#1d4ed8" },
        logo: BitbucketLogo,
        columns: { name: "Repository", key: "Branch" },
        connectedColumnLabel: "Repository · Branch",
        connectField: {
            label: "Workspace (Optional)", placeholder: "your-workspace",
            helperText: "Enter your Bitbucket workspace name (optional)", required: false
        }
    }
];
